(() => {
  'use strict';

  const cfg = window.DRVN_CONFIG || {};
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  function toast(message, type = 'success') {
    const region = $('#drvn-toast-region');
    if (!region) return;
    const node = document.createElement('div');
    node.className = `toast toast--${type}`;
    node.textContent = message;
    region.appendChild(node);
    setTimeout(() => node.remove(), 4200);
  }

  function escapeHtml(value = '') {
    return String(value).replace(/[&<>'"]/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));
  }

  async function api(path, options = {}) {
    const response = await fetch(`${cfg.proxy || '/apps/drvn'}${path}`, {
      credentials: 'same-origin',
      headers: {'Accept': 'application/json', ...(options.body instanceof FormData ? {} : {'Content-Type': 'application/json'}), ...(options.headers || {})},
      ...options
    });
    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await response.json() : {message: await response.text()};
    if (!response.ok) throw new Error(data.message || data.error || 'Something went wrong.');
    return data;
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat(undefined, {style: 'currency', currency: cfg.currency || 'AUD'}).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    const toRad = (v) => (v * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function savedGuestIds() {
    try { return JSON.parse(localStorage.getItem('drvn_saved_cars') || '[]'); } catch (_) { return []; }
  }

  function setSavedGuestIds(ids) {
    localStorage.setItem('drvn_saved_cars', JSON.stringify([...new Set(ids)]));
  }

  function setFavoriteButton(button, saved) {
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? 'Remove from saved cars' : 'Save car');
    const label = button.querySelector('[data-favorite-label]');
    if (label) label.textContent = saved ? 'Saved' : 'Save';
  }

  async function initialiseFavorites() {
    const buttons = $$('[data-favorite-button]');
    if (!buttons.length) return;
    const guest = savedGuestIds();
    buttons.forEach((button) => setFavoriteButton(button, guest.includes(button.dataset.productId)));

    if (cfg.customerId) {
      try {
        const data = await api('/saves');
        const ids = data.items.map((item) => String(item.shopifyProductId));
        if (guest.length) {
          await api('/saves/sync', {method: 'POST', body: JSON.stringify({productIds: guest})});
          ids.push(...guest);
          localStorage.removeItem('drvn_saved_cars');
        }
        const merged = [...new Set(ids)];
        buttons.forEach((button) => setFavoriteButton(button, merged.includes(String(button.dataset.productId))));
      } catch (error) { console.warn(error); }
    }
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-favorite-button]');
    if (!button) return;
    event.preventDefault();
    const productId = String(button.dataset.productId);
    const saved = button.classList.contains('is-saved');
    button.disabled = true;
    try {
      if (cfg.customerId) {
        await api(`/saves/${encodeURIComponent(productId)}`, {method: saved ? 'DELETE' : 'POST', body: saved ? undefined : '{}'});
      } else {
        const ids = savedGuestIds().filter((id) => id !== productId);
        if (!saved) ids.push(productId);
        setSavedGuestIds(ids);
        toast(saved ? 'Removed from saved cars.' : 'Saved on this device. Sign in to sync it.');
      }
      $$(`[data-favorite-button][data-product-id="${CSS.escape(productId)}"]`).forEach((item) => setFavoriteButton(item, !saved));
    } catch (error) { toast(error.message, 'error'); }
    finally { button.disabled = false; }
  });

  class MarketplaceSearch {
    constructor(root) {
      this.root = root;
      this.form = $('[data-marketplace-filter]', root);
      this.grid = $('[data-results-grid]', root);
      this.count = $('[data-results-count]', root);
      this.status = $('[data-marketplace-status]', root);
      this.lockedCategory = String(root.dataset.marketplaceCategoryLock || '').trim();
      this.lat = null;
      this.lng = null;
      this.page = 1;
      this.map = null;
      this.mapMarkers = [];
      this.lastItems = [];
      this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.page = 1; this.search(); });
      $('[data-use-location]', root)?.addEventListener('click', () => this.useLocation());
      $('[data-reset-filters]', root)?.addEventListener('click', () => this.reset());
      $('[data-view-grid]', root)?.addEventListener('click', () => root.classList.remove('is-map-view'));
      $('[data-view-map]', root)?.addEventListener('click', () => {
        root.classList.add('is-map-view');
        this.renderMap(this.lastItems);
      });
      root.addEventListener('click', (e) => {
        const pageButton = e.target.closest('[data-search-page]');
        if (pageButton) { this.page = Number(pageButton.dataset.searchPage || 1); this.search(); }
      });
      this.applyUrlState();
    }

    applyUrlState() {
      if (!this.form) return;
      const params = new URLSearchParams(window.location.search);
      ['q','location','radius','make','category','service','state','sort'].forEach((name) => {
        const field = this.form.elements.namedItem(name);
        if (field && params.has(name)) field.value = params.get(name);
      });
      if (this.lockedCategory) {
        const categoryField = this.form.elements.namedItem('category');
        if (categoryField) categoryField.value = this.lockedCategory;
      }
      this.page = Math.max(1, Number(params.get('page') || 1));
      window.setTimeout(() => this.search(), 0);
    }

    updateUrl() {
      const params = new URLSearchParams();
      const data = new FormData(this.form);
      for (const [key, value] of data.entries()) if (String(value).trim()) params.set(key, value);
      if (this.lockedCategory) params.delete('category');
      if (this.page > 1) params.set('page', String(this.page));
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', next);
    }

    setStatus(message) { if (this.status) this.status.textContent = message || ''; }

    async useLocation() {
      if (!navigator.geolocation) return toast('Location is not supported by this browser.', 'error');
      this.setStatus('Finding your location…');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.lat = position.coords.latitude;
          this.lng = position.coords.longitude;
          const field = $('[name="location"]', this.form);
          if (field) field.value = 'Current location';
          this.setStatus('Location ready. Showing cars that can travel to you.');
          this.search();
        },
        () => { this.setStatus(''); toast('We could not access your location. Enter a suburb or postcode instead.', 'error'); },
        {enableHighAccuracy: false, timeout: 10000, maximumAge: 300000}
      );
    }

    async resolveLocation(query) {
      if (!query || query.toLowerCase() === 'current location') return;
      const result = await api(`/geocode?q=${encodeURIComponent(query)}`);
      this.lat = result.latitude;
      this.lng = result.longitude;
    }

    params() {
      const data = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) if (String(value).trim()) params.set(key, value);
      if (this.lockedCategory) params.set('category', this.lockedCategory);
      if (this.lat != null && this.lng != null) { params.set('lat', this.lat); params.set('lng', this.lng); }
      params.set('page', String(this.page));
      return params;
    }

    async search() {
      if (!this.form || !this.grid) return;
      const location = $('[name="location"]', this.form)?.value?.trim();
      this.page = Number(this.page || 1);
      try {
        this.setStatus('Searching available vehicles…');
        if (!location) { this.lat = null; this.lng = null; }
        if (location && location.toLowerCase() !== 'current location' && (this.lat == null || $('[name="location"]', this.form).dataset.lastValue !== location)) {
          await this.resolveLocation(location);
          $('[name="location"]', this.form).dataset.lastValue = location;
        }
        const data = await api(`/search?${this.params().toString()}`);
        this.render(data);
        this.updateUrl();
        this.setStatus(data.locationLabel ? `Distances measured from ${data.locationLabel}.` : '');
      } catch (error) {
        console.warn(error);
        this.fallbackFilter();
        this.setStatus('Using the listings already loaded on this page. Connect the companion app for full radius search.');
      }
    }

    fallbackFilter() {
      const radius = Number($('[name="radius"]', this.form)?.value || 99999);
      const keyword = ($('[name="q"]', this.form)?.value || '').toLowerCase();
      const make = ($('[name="make"]', this.form)?.value || '').toLowerCase();
      const category = (this.lockedCategory || $('[name="category"]', this.form)?.value || '').toLowerCase();
      const service = ($('[name="service"]', this.form)?.value || '').toLowerCase();
      const state = ($('[name="state"]', this.form)?.value || '').toLowerCase();
      let visible = 0;
      $$('.vehicle-card', this.grid).forEach((card) => {
        const distance = this.lat != null && card.dataset.lat ? haversineKm(this.lat, this.lng, Number(card.dataset.lat), Number(card.dataset.lng)) : 0;
        const ownerRadius = Number(card.dataset.travelRadius || 0);
        const nationwide = card.dataset.nationwide === 'true';
        const matchesDistance = this.lat == null || (distance <= radius && (nationwide || distance <= ownerRadius));
        const haystack = `${card.dataset.title || ''} ${card.dataset.make || ''} ${card.dataset.model || ''}`.toLowerCase();
        const matchesKeyword = !keyword || haystack.includes(keyword);
        const matchesMake = !make || (card.dataset.make || '').toLowerCase() === make;
        const matchesCategory = !category || (card.dataset.categories || '').toLowerCase().includes(category);
        const matchesService = !service || (card.dataset.services || '').toLowerCase().includes(service);
        const matchesState = !state || (card.dataset.state || '').toLowerCase() === state;
        const show = matchesDistance && matchesKeyword && matchesMake && matchesCategory && matchesService && matchesState;
        card.hidden = !show;
        if (show) visible += 1;
      });
      if (this.count) this.count.textContent = String(visible);
    }

    render(data) {
      this.lastItems = data.items || [];
      this.grid.innerHTML = data.items.length ? data.items.map((item) => this.card(item)).join('') : '<div class="empty-state" style="grid-column:1/-1"><h2>No matching cars yet</h2><p class="muted">Try expanding the distance or removing a filter.</p></div>';
      if (this.count) this.count.textContent = String(data.total || 0);
      const pager = $('[data-marketplace-pagination]', this.root);
      if (pager) {
        pager.innerHTML = '';
        const totalPages = Math.max(1, data.totalPages || 1);
        if (totalPages > 1) {
          for (let p = 1; p <= totalPages; p += 1) pager.insertAdjacentHTML('beforeend', `<button class="${p === data.page ? 'current' : ''}" data-search-page="${p}">${p}</button>`);
        }
      }
      initialiseFavorites();
      if (this.root.classList.contains('is-map-view')) this.renderMap(this.lastItems);
    }

    renderMap(items) {
      const mapShell = $('[data-results-map]', this.root);
      const canvas = $('[data-map-canvas]', this.root);
      const message = $('[data-map-message]', this.root);
      if (!mapShell || !canvas) return;
      if (!cfg.mapboxToken || !window.mapboxgl) {
        if (message) message.hidden = false;
        return;
      }
      if (message) message.hidden = true;
      window.mapboxgl.accessToken = cfg.mapboxToken;
      if (!this.map) {
        this.map = new window.mapboxgl.Map({
          container: canvas,
          style: 'mapbox://styles/mapbox/dark-v11',
          center: [133.7751, -25.2744],
          zoom: 3,
          cooperativeGestures: true
        });
        this.map.addControl(new window.mapboxgl.NavigationControl({showCompass:false}), 'top-right');
      }
      this.mapMarkers.forEach((marker) => marker.remove());
      this.mapMarkers = [];
      const bounds = new window.mapboxgl.LngLatBounds();
      (items || []).forEach((item) => {
        if (!Number.isFinite(Number(item.latitude)) || !Number.isFinite(Number(item.longitude))) return;
        const popup = new window.mapboxgl.Popup({offset: 22}).setHTML(`<div class="map-popup"><strong>${escapeHtml(item.title)}</strong><span>${escapeHtml(item.publicLocation || '')}</span><a href="${escapeHtml(item.url)}">View car</a></div>`);
        const marker = new window.mapboxgl.Marker({color: '#ff3030'}).setLngLat([Number(item.longitude), Number(item.latitude)]).setPopup(popup).addTo(this.map);
        this.mapMarkers.push(marker);
        bounds.extend([Number(item.longitude), Number(item.latitude)]);
      });
      window.setTimeout(() => {
        this.map.resize();
        if (this.mapMarkers.length === 1) this.map.flyTo({center: bounds.getCenter(), zoom: 8});
        else if (this.mapMarkers.length > 1) this.map.fitBounds(bounds, {padding: 56, maxZoom: 9});
      }, 0);
    }

    card(item) {
      const services = (item.services || []).slice(0, 3).map((service) => `<span class="pill">${escapeHtml(service)}</span>`).join('');
      const distance = item.distanceKm == null ? '' : `<span>${Math.round(item.distanceKm)} km away</span>`;
      return `<article class="vehicle-card card" data-product-id="${escapeHtml(item.shopifyProductId)}" data-lat="${escapeHtml(item.latitude ?? '')}" data-lng="${escapeHtml(item.longitude ?? '')}" data-travel-radius="${escapeHtml(item.travelRadiusKm ?? '')}" data-nationwide="${item.nationwide ? 'true' : 'false'}" data-title="${escapeHtml(item.title || '')}" data-make="${escapeHtml(item.make || '')}" data-model="${escapeHtml(item.model || '')}" data-state="${escapeHtml(item.state || '')}" data-services="${escapeHtml((item.services || []).join(','))}" data-categories="${escapeHtml((item.categories || []).join(','))}">
        <a class="vehicle-card__media" href="${escapeHtml(item.url)}">
          <img src="${escapeHtml(item.image || '')}" alt="${escapeHtml(item.title)}" loading="lazy" width="900" height="675">
          <div class="vehicle-card__badges">${item.verified ? '<span class="badge badge--success">Verified</span>' : ''}${item.featured ? '<span class="badge">Featured</span>' : ''}</div>
        </a>
        <button class="vehicle-card__favorite icon-button favorite-button" data-favorite-button data-product-id="${escapeHtml(item.shopifyProductId)}" aria-label="Save car" aria-pressed="false">♡</button>
        <div class="vehicle-card__body">
          <div class="vehicle-card__meta"><span>${escapeHtml(item.publicLocation || '')}</span>${distance}</div>
          <h3 class="vehicle-card__title"><a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a></h3>
          <div class="vehicle-card__services">${services}</div>
          <div class="vehicle-card__footer"><div class="vehicle-card__price"><span>Unlock owner details</span><strong>${money(item.unlockPriceCents)}</strong></div><a class="button button--secondary" href="${escapeHtml(item.url)}">View car</a></div>
        </div>
      </article>`;
    }

    reset() {
      this.form.reset(); this.lat = null; this.lng = null; this.page = 1;
      if (this.lockedCategory) {
        const categoryField = this.form.elements.namedItem('category');
        if (categoryField) categoryField.value = this.lockedCategory;
      }
      this.search();
    }
  }

  class ContactUnlock {
    constructor(root) {
      this.root = root;
      this.productId = root.dataset.productId;
      this.content = $('[data-contact-content]', root);
      this.locked = $('[data-contact-locked]', root);
      this.checking = $('[data-contact-checking]', root);
      this.purchase = $('[data-contact-purchase-panel]', root);
      if (cfg.customerId) this.load();
    }
    showPurchase() {
      this.checking?.setAttribute('hidden', 'hidden');
      this.purchase?.removeAttribute('hidden');
    }
    async load() {
      try {
        const data = await api(`/contact/${encodeURIComponent(this.productId)}`);
        if (!data.unlocked) { this.showPurchase(); return; }
        this.locked?.setAttribute('hidden', 'hidden');
        this.content?.removeAttribute('hidden');
        this.content.innerHTML = [
          ['Owner', data.contact.displayName],
          ['Phone', data.contact.phone ? `<a href="tel:${escapeHtml(data.contact.phone)}">${escapeHtml(data.contact.phone)}</a>` : 'Not supplied'],
          ['Email', data.contact.email ? `<a href="mailto:${escapeHtml(data.contact.email)}">${escapeHtml(data.contact.email)}</a>` : 'Not supplied'],
          ['Instagram', data.contact.instagram ? `<a href="${escapeHtml(data.contact.instagramUrl)}" target="_blank" rel="noopener">${escapeHtml(data.contact.instagram)}</a>` : 'Not supplied']
        ].map(([label,value]) => `<div class="contact-row"><span>${label}</span><div>${value}</div></div>`).join('');
        api('/events', {method:'POST', body:JSON.stringify({type:'CONTACT_REVEAL', productId:this.productId})}).catch(()=>{});
      } catch (error) { console.warn(error); this.showPurchase(); }
    }
  }


  function reviewStars(rating) {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return `<span class="review-stars" aria-label="${rounded} out of 5 stars">${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}</span>`;
  }

  class ReviewsPanel {
    constructor(root) {
      this.root = root;
      this.productId = root.dataset.productId;
      root.addEventListener('submit', (event) => this.submit(event));
      this.load();
    }
    async load() {
      try {
        const data = await api(`/reviews/${encodeURIComponent(this.productId)}`);
        this.render(data);
      } catch (error) {
        this.root.innerHTML = `<p class="eyebrow">Owner reviews</p><div class="empty-state"><p class="muted">Reviews are temporarily unavailable.</p></div>`;
      }
    }
    render(data) {
      const own = data.ownReview || {};
      const options = [5,4,3,2,1].map((rating) => `<option value="${rating}" ${Number(own.rating) === rating ? 'selected' : ''}>${rating} star${rating === 1 ? '' : 's'}</option>`).join('');
      const form = data.canReview ? `<form class="review-form card" data-review-form>
        <div><p class="eyebrow">Verified contact purchaser</p><h3>${own.status ? 'Update your review' : 'Share your experience'}</h3>${own.status ? `<p class="field-help">Current moderation status: ${escapeHtml(own.status.toLowerCase())}</p>` : ''}</div>
        <div class="form-grid"><div><label class="field-label">Rating</label><select class="select" name="rating" required>${options}</select></div><div><label class="field-label">Review title</label><input class="field" name="title" maxlength="100" value="${escapeHtml(own.title || '')}" placeholder="Professional and easy to deal with"></div></div>
        <label class="field-label">Review</label><textarea class="textarea" name="body" minlength="10" maxlength="2000" required>${escapeHtml(own.body || '')}</textarea>
        <button class="button button--primary" type="submit">Submit review</button>
      </form>` : `<div class="legal-note">Only signed-in customers who have purchased this owner's contact details can submit a review.</div>`;
      const items = (data.items || []).length ? `<div class="review-list">${data.items.map((item) => `<article class="review-card card"><div class="review-card__top">${reviewStars(item.rating)}<span class="muted small">Verified contact purchaser · ${escapeHtml(new Date(item.createdAt).toLocaleDateString())}</span></div>${item.title ? `<h3>${escapeHtml(item.title)}</h3>` : ''}<p>${escapeHtml(item.body)}</p></article>`).join('')}</div>` : '<div class="empty-state"><h3>No published reviews yet</h3><p class="muted">Be the first verified purchaser to share an experience.</p></div>';
      this.root.innerHTML = `<div class="review-summary"><div><p class="eyebrow">Owner reviews</p><h2 class="h2">${data.count ? `${Number(data.average).toFixed(1)} / 5` : 'New listing'}</h2></div><div>${reviewStars(data.average)}<span class="muted small">${Number(data.count || 0)} published review${Number(data.count || 0) === 1 ? '' : 's'}</span></div></div>${items}${form}`;
    }
    async submit(event) {
      const form = event.target.closest('[data-review-form]');
      if (!form) return;
      event.preventDefault();
      const button = $('[type="submit"]', form);
      button.disabled = true;
      try {
        const values = Object.fromEntries(new FormData(form).entries());
        await api('/reviews', {method:'POST', body:JSON.stringify({...values, productId:this.productId})});
        toast('Review submitted for moderation.');
        await this.load();
      } catch (error) {
        toast(error.message, 'error');
        button.disabled = false;
      }
    }
  }

  class ReportForm {
    constructor(form) {
      this.form = form;
      form.addEventListener('submit', (event) => this.submit(event));
    }
    async submit(event) {
      event.preventDefault();
      if (!this.form.reportValidity()) return;
      const button = $('[type="submit"]', this.form);
      button.disabled = true;
      try {
        const values = Object.fromEntries(new FormData(this.form).entries());
        await api('/reports', {method:'POST', body:JSON.stringify({...values, productId:this.form.dataset.productId})});
        toast('Listing report received. Thank you.');
        this.form.reset();
        this.form.closest('details')?.removeAttribute('open');
      } catch (error) {
        toast(error.message, 'error');
      } finally {
        button.disabled = false;
      }
    }
  }


  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share-listing]');
    if (!button) return;
    const payload = {title: document.title, text: 'Take a look at this vehicle listing.', url: window.location.href};
    try {
      if (navigator.share) await navigator.share(payload);
      else {
        await navigator.clipboard.writeText(window.location.href);
        toast('Listing link copied.');
      }
      api('/events', {method:'POST', body:JSON.stringify({type:'SHARE', productId:button.dataset.productId})}).catch(()=>{});
    } catch (error) {
      if (error?.name !== 'AbortError') toast('Could not share this listing.', 'error');
    }
  });

  function listingHtml(item, owner = false) {
    const metrics = owner ? `<div class="metric-row"><span>${item.views || 0} views</span><span>${item.saves || 0} saves</span><span>${item.unlocks || 0} unlocks</span></div>` : '';
    const status = owner ? `<div class="dashboard-status"><span class="status-dot status-dot--${escapeHtml(String(item.status || '').toLowerCase())}"></span>${escapeHtml(String(item.status || ''))}${item.pauseReason === 'membership' ? ' · membership paused' : ''}</div>` : '';
    const controls = owner
      ? `<div class="dashboard-actions"><a class="button button--secondary" href="${escapeHtml(item.url)}">View</a><button class="button button--secondary" type="button" data-listing-action="edit" data-listing-id="${escapeHtml(item.id)}">Edit</button>${item.status === 'PAUSED' ? `<button class="button button--primary" type="button" data-listing-action="resume" data-listing-id="${escapeHtml(item.id)}">Resume</button>` : (['PENDING','PUBLISHED'].includes(item.status) ? `<button class="button button--ghost" type="button" data-listing-action="pause" data-listing-id="${escapeHtml(item.id)}">Pause</button>` : '')}</div>`
      : `<a class="button button--secondary" href="${escapeHtml(item.url)}">View</a>`;
    return `<article class="dashboard-listing card"><img src="${escapeHtml(item.image || '')}" alt="" loading="lazy"><div><h3>${escapeHtml(item.title)}</h3><div class="muted small">${escapeHtml(item.publicLocation || '')}</div>${status}${metrics}</div>${controls}</article>`;
  }

  function unlockedListingHtml(item) {
    const contact = item.contact || {};
    const rows = [
      ['Owner', escapeHtml(contact.displayName || 'Independent owner')],
      ['Phone', contact.phone ? `<a href="tel:${escapeHtml(contact.phone)}">${escapeHtml(contact.phone)}</a>` : 'Not supplied'],
      ['Email', contact.email ? `<a href="mailto:${escapeHtml(contact.email)}">${escapeHtml(contact.email)}</a>` : 'Not supplied'],
      ['Instagram', contact.instagram ? `<a href="${escapeHtml(contact.instagramUrl || '#')}" target="_blank" rel="noopener">${escapeHtml(contact.instagram)}</a>` : 'Not supplied']
    ].map(([label, value]) => `<div class="dashboard-contact-row"><span>${label}</span><div>${value}</div></div>`).join('');
    return `<article class="dashboard-unlock card"><div class="dashboard-unlock__vehicle"><img src="${escapeHtml(item.image || '')}" alt="" loading="lazy"><div><h3>${escapeHtml(item.title)}</h3><div class="muted small">${escapeHtml(item.publicLocation || '')}</div><div class="muted small">Unlocked ${item.unlockedAt ? escapeHtml(new Date(item.unlockedAt).toLocaleDateString()) : ''}</div></div><a class="button button--secondary" href="${escapeHtml(item.url)}">View listing</a></div><div class="dashboard-contact-grid">${rows}</div></article>`;
  }

  class CustomerDashboard {
    constructor(root) { this.root = root; this.load(); }
    async load() {
      if (!cfg.customerId) { this.root.innerHTML = `<div class="empty-state"><h2>Sign in to view your marketplace account</h2><a class="button button--primary" href="${escapeHtml(cfg.accountUrl || '/account')}">Sign in</a></div>`; return; }
      try {
        const data = await api('/customer/dashboard');
        this.root.innerHTML = `<div class="tabs"><button class="tab-button is-active" data-dashboard-tab="saved">Saved cars</button><button class="tab-button" data-dashboard-tab="unlocked">Unlocked contacts</button><button class="tab-button" data-dashboard-tab="recent">Recently viewed</button></div>
          <div data-dashboard-panel="saved" class="dashboard-list">${data.saved.length ? data.saved.map((item)=>listingHtml(item)).join('') : '<div class="empty-state"><h3>No saved cars yet</h3></div>'}</div>
          <div data-dashboard-panel="unlocked" class="dashboard-list" hidden>${data.unlocked.length ? data.unlocked.map((item)=>unlockedListingHtml(item)).join('') : '<div class="empty-state"><h3>No contacts unlocked yet</h3></div>'}</div>
          <div data-dashboard-panel="recent" class="dashboard-list" hidden>${data.recent.length ? data.recent.map((item)=>listingHtml(item)).join('') : '<div class="empty-state"><h3>No recently viewed cars yet</h3></div>'}</div>`;
        this.root.addEventListener('click',(e)=>{const tab=e.target.closest('[data-dashboard-tab]');if(!tab)return;$$('[data-dashboard-tab]',this.root).forEach(x=>x.classList.toggle('is-active',x===tab));$$('[data-dashboard-panel]',this.root).forEach(x=>x.hidden=x.dataset.dashboardPanel!==tab.dataset.dashboardTab);});
      } catch (error) { this.root.innerHTML = `<div class="empty-state"><h3>Dashboard unavailable</h3><p class="muted">${escapeHtml(error.message)}</p></div>`; }
    }
  }

  const OWNER_EDIT_SERVICES = ['Wedding','Formal','Photoshoot','Static Display','Film & TV','Brand Event','Content Creation','Transport'];
  const OWNER_EDIT_CATEGORIES = ['Luxury','Modified','JDM','Australian Muscle','Classic','Supercar','Race & Track','American','Euro','Exotic'];

  function checkboxOptions(name, options, selected = []) {
    return options.map((option) => `<label class="check-card"><input type="checkbox" name="${escapeHtml(name)}" value="${escapeHtml(option)}" ${selected.includes(option) ? 'checked' : ''}><span><strong>${escapeHtml(option)}</strong></span></label>`).join('');
  }

  class OwnerDashboard {
    constructor(root) {
      this.root = root;
      this.root.addEventListener('click', (event) => this.handleClick(event));
      this.root.addEventListener('submit', (event) => this.handleSubmit(event));
      this.load();
    }
    editor() { return $('[data-owner-editor]', this.root); }
    closeEditor() {
      const editor = this.editor();
      if (editor) { editor.hidden = true; editor.innerHTML = ''; }
    }
    async handleClick(event) {
      const close = event.target.closest('[data-close-owner-editor]');
      if (close) { this.closeEditor(); return; }
      const profile = event.target.closest('[data-owner-profile]');
      if (profile) { await this.openProfile(profile); return; }
      const button = event.target.closest('[data-listing-action]');
      if (!button) return;
      const action = button.dataset.listingAction;
      if (action === 'edit') { await this.openEditor(button.dataset.listingId, button); return; }
      button.disabled = true;
      try {
        await api(`/listings/${encodeURIComponent(button.dataset.listingId)}/${action}`, {method:'POST', body:'{}'});
        toast(action === 'pause' ? 'Listing paused.' : 'Listing resumed.');
        await this.load();
      } catch (error) {
        toast(error.message, 'error');
        button.disabled = false;
      }
    }
    async openEditor(id, button) {
      button.disabled = true;
      try {
        const data = await api(`/listings/${encodeURIComponent(id)}/edit`);
        const item = data.listing; const owner = data.owner;
        const stateOptions = ['QLD','NSW','VIC','SA','WA','TAS','ACT','NT'].map((state) => `<option ${item.state === state ? 'selected' : ''}>${state}</option>`).join('');
        const transmissionOptions = ['Automatic','Manual','Sequential','Other'].map((value) => `<option ${item.transmission === value ? 'selected' : ''}>${value}</option>`).join('');
        const editor = this.editor();
        editor.hidden = false;
        editor.innerHTML = `<form class="owner-editor card" data-owner-edit-listing data-listing-id="${escapeHtml(item.id)}">
          <div class="owner-editor__heading"><div><p class="eyebrow">Edit vehicle</p><h2 class="h2">${escapeHtml(item.title)}</h2><p class="muted">Material changes return an active listing to moderation before it becomes public again. Existing gallery images remain in place.</p></div><button class="button button--ghost" type="button" data-close-owner-editor>Close</button></div>
          <div class="form-grid"><div><label class="field-label">Public display name</label><input class="field" name="displayName" value="${escapeHtml(owner.displayName)}" required></div><div><label class="field-label">Contact phone</label><input class="field" name="phone" type="tel" value="${escapeHtml(owner.phone)}" required></div><div><label class="field-label">Contact email</label><input class="field" name="email" type="email" value="${escapeHtml(owner.email)}" required></div><div><label class="field-label">Instagram</label><input class="field" name="instagram" value="${escapeHtml(owner.instagram)}"></div></div>
          <div class="form-grid form-grid--3"><div><label class="field-label">Year</label><input class="field" name="year" type="number" value="${escapeHtml(item.year || '')}" required></div><div><label class="field-label">Make</label><input class="field" name="make" value="${escapeHtml(item.make)}" required></div><div><label class="field-label">Model</label><input class="field" name="model" value="${escapeHtml(item.model)}" required></div><div class="form-span-2"><label class="field-label">Listing title</label><input class="field" name="title" value="${escapeHtml(item.title)}" required></div><div><label class="field-label">Colour</label><input class="field" name="colour" value="${escapeHtml(item.colour)}"></div><div><label class="field-label">Power</label><input class="field" name="power" value="${escapeHtml(item.power)}"></div><div><label class="field-label">Seats</label><input class="field" name="seats" type="number" min="0" max="12" value="${escapeHtml(item.seats ?? '')}"></div><div><label class="field-label">Transmission</label><select class="select" name="transmission">${transmissionOptions}</select></div><div class="form-span-2"><label class="field-label">Description</label><textarea class="textarea" name="description" required>${escapeHtml(item.description)}</textarea></div></div>
          <div class="form-grid form-grid--3"><div><label class="field-label">Public suburb or region</label><input class="field" name="publicLocation" value="${escapeHtml(item.publicLocation)}" required></div><div><label class="field-label">State</label><select class="select" name="state" required>${stateOptions}</select></div><div><label class="field-label">Postcode</label><input class="field" name="postcode" value="${escapeHtml(item.postcode)}" required></div><div><label class="field-label">Travel radius</label><input class="field" name="travelRadiusKm" type="number" min="1" max="5000" value="${escapeHtml(item.travelRadiusKm)}" required></div><div><label class="field-label">Australia-wide travel</label><select class="select" name="nationwide"><option value="false" ${item.nationwide ? '' : 'selected'}>No</option><option value="true" ${item.nationwide ? 'selected' : ''}>Yes</option></select></div><div><label class="field-label">DRVN contact unlock fee</label><div class="field" aria-readonly="true" style="display:flex;align-items:center;font-weight:850">$1.99 AUD</div><p class="muted small" style="margin:8px 0 0">Fixed by DRVN and paid to DRVN. Owners cannot change this fee.</p></div></div>
          <div><p class="field-label">Available for</p><div class="checkbox-grid">${checkboxOptions('services', OWNER_EDIT_SERVICES, item.services)}</div></div>
          <div><p class="field-label">Vehicle style</p><div class="checkbox-grid">${checkboxOptions('categories', OWNER_EDIT_CATEGORIES, item.categories)}</div></div>
          <div class="legal-note">For gallery replacements, use the Shopify product media editor or have an administrator review new images before publishing.</div>
          <button class="button button--primary" type="submit">Save and send for review</button>
        </form>`;
        editor.scrollIntoView({behavior:'smooth', block:'start'});
      } catch (error) { toast(error.message, 'error'); }
      finally { button.disabled = false; }
    }
    async openProfile(button) {
      button.disabled = true;
      try {
        const profile = await api('/owner/profile');
        const editor = this.editor();
        editor.hidden = false;
        editor.innerHTML = `<form class="owner-editor card" data-owner-profile-form><div class="owner-editor__heading"><div><p class="eyebrow">Private contact profile</p><h2 class="h2">Owner details</h2><p class="muted">These details are revealed only to customers with a paid contact entitlement. Your display name is public.</p></div><button class="button button--ghost" type="button" data-close-owner-editor>Close</button></div><div class="form-grid"><div><label class="field-label">Public display name</label><input class="field" name="displayName" value="${escapeHtml(profile.displayName)}" required></div><div><label class="field-label">Phone</label><input class="field" name="phone" type="tel" value="${escapeHtml(profile.phone)}" required></div><div><label class="field-label">Email</label><input class="field" name="email" type="email" value="${escapeHtml(profile.email)}" required></div><div><label class="field-label">Instagram</label><input class="field" name="instagram" value="${escapeHtml(profile.instagram)}"></div></div><button class="button button--primary" type="submit">Save contact profile</button></form>`;
        editor.scrollIntoView({behavior:'smooth', block:'start'});
      } catch (error) { toast(error.message, 'error'); }
      finally { button.disabled = false; }
    }
    async handleSubmit(event) {
      const listingForm = event.target.closest('[data-owner-edit-listing]');
      const profileForm = event.target.closest('[data-owner-profile-form]');
      const form = listingForm || profileForm;
      if (!form) return;
      event.preventDefault();
      if (!form.reportValidity()) return;
      const button = $('[type="submit"]', form); button.disabled = true;
      try {
        const fields = new FormData(form); const payload = Object.fromEntries(fields.entries());
        if (listingForm) {
          payload.services = fields.getAll('services'); payload.categories = fields.getAll('categories'); payload.nationwide = fields.get('nationwide') === 'true';
          if (!payload.services.length || !payload.categories.length) throw new Error('Choose at least one service and one vehicle style.');
          const result = await api(`/listings/${encodeURIComponent(form.dataset.listingId)}`, {method:'PATCH', body:JSON.stringify(payload)});
          toast(result.message || 'Listing updated.');
        } else {
          await api('/owner/profile', {method:'PATCH', body:JSON.stringify(payload)});
          toast('Contact profile updated.');
        }
        await this.load();
      } catch (error) { toast(error.message, 'error'); button.disabled = false; }
    }
    async load() {
      if (!cfg.customerId) { this.root.innerHTML = `<div class="empty-state"><h2>Sign in to manage your garage</h2><a class="button button--primary" href="${escapeHtml(cfg.accountUrl || '/account')}">Sign in</a></div>`; return; }
      try {
        const data = await api('/owner/dashboard');
        const membership = data.membership || {tier:data.membershipTier,activeListings:0,limit:0,remaining:0};
        const planUsage = membership.limit ? `${membership.activeListings} of ${membership.limit} active listings · ${membership.remaining} slot${membership.remaining === 1 ? '' : 's'} available` : 'Choose a membership before submitting a vehicle';
        const recent = data.recent30Days || {views:0,saves:0,unlocks:0,conversion:'0.0'};
        this.root.innerHTML = `<div data-owner-editor hidden></div><div class="dashboard-stats"><div class="stat-card card"><span>All-time views</span><strong>${data.summary.views}</strong></div><div class="stat-card card"><span>Current saves</span><strong>${data.summary.saves}</strong></div><div class="stat-card card"><span>Contact unlocks</span><strong>${data.summary.unlocks}</strong></div><div class="stat-card card"><span>Conversion</span><strong>${data.summary.conversion}%</strong></div></div>
          <div class="analytics-window card"><div><span>Last 30 days</span><strong>${recent.views} views</strong></div><div><span>New saves</span><strong>${recent.saves}</strong></div><div><span>New unlocks</span><strong>${recent.unlocks}</strong></div><div><span>Conversion</span><strong>${recent.conversion}%</strong></div></div>
          <div class="membership-summary card"><div><span>Current listing plan</span><strong>${escapeHtml(membership.tier || 'No active membership')}</strong><small>${escapeHtml(planUsage)}</small></div><button class="button button--secondary" type="button" data-owner-profile>Contact profile</button></div><div class="section-heading"><div><p class="eyebrow">Your garage</p><h2 class="h2">Vehicle listings</h2></div><a class="button button--primary" href="/pages/list-your-car">Add a vehicle</a></div>
          <div class="dashboard-list">${data.listings.length ? data.listings.map((item)=>listingHtml(item,true)).join('') : '<div class="empty-state"><h3>No vehicles listed</h3><p class="muted">Choose a membership and submit your first vehicle.</p><a class="button button--primary" href="/pages/list-your-car">List your car</a></div>'}</div>`;
      } catch (error) { this.root.innerHTML = `<div class="empty-state"><h3>Owner dashboard unavailable</h3><p class="muted">${escapeHtml(error.message)}</p></div>`; }
    }
  }

  async function imageSource(file) {
    if ('createImageBitmap' in window) return createImageBitmap(file, {imageOrientation: 'from-image'});
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
      image.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`Could not read ${file.name}.`)); };
      image.src = url;
    });
  }

  async function compressListingImage(file, index) {
    if (!['image/jpeg','image/png','image/webp'].includes(file.type)) throw new Error(`${file.name} is not a supported image type.`);
    if (file.size > 20 * 1024 * 1024) throw new Error(`${file.name} is larger than 20 MB.`);
    try {
      const source = await imageSource(file);
      const width = source.width;
      const height = source.height;
      const scale = Math.min(1, 2400 / Math.max(width, height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext('2d', {alpha: false});
      context.drawImage(source, 0, 0, canvas.width, canvas.height);
      if (typeof source.close === 'function') source.close();
      const blob = await new Promise((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Image compression failed.')), 'image/jpeg', 0.86));
      if (blob.size > 8 * 1024 * 1024) throw new Error(`${file.name} is still too large after optimisation.`);
      const base = file.name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '') || `vehicle-${index + 1}`;
      return new File([blob], `${base}.jpg`, {type: 'image/jpeg', lastModified: Date.now()});
    } catch (error) {
      if (file.size <= 8 * 1024 * 1024) return file;
      throw error;
    }
  }

  class ListingForm {
    constructor(form) {
      this.form = form;
      this.submit = $('[type="submit"]', form);
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
    async handleSubmit(event) {
      event.preventDefault();
      if (!cfg.customerId) return toast(cfg.loginRequiredMessage || 'Please sign in first.', 'error');
      if (!this.form.reportValidity()) return;
      this.submit.disabled = true;
      const original = this.submit.textContent;
      this.submit.textContent = 'Submitting…';
      try {
        const source = new FormData(this.form);
        const files = source.getAll('photos').filter((item) => item instanceof File && item.size > 0);
        if (files.length < 3 || files.length > 12) throw new Error('Upload between 3 and 12 clear vehicle photos.');
        const payload = new FormData();
        for (const [key, value] of source.entries()) if (key !== 'photos') payload.append(key, value);
        for (let index = 0; index < files.length; index += 1) {
          this.submit.textContent = `Optimising photo ${index + 1} of ${files.length}…`;
          payload.append('photos', await compressListingImage(files[index], index));
        }
        this.submit.textContent = 'Creating your listing…';
        const data = await api('/listings', {method:'POST', body:payload});
        toast('Your vehicle was submitted for review.');
        this.form.reset();
        if (data.dashboardUrl) setTimeout(()=>{window.location.href=data.dashboardUrl;},900);
      } catch (error) { toast(error.message, 'error'); }
      finally { this.submit.disabled = false; this.submit.textContent = original; }
    }
  }

  function trackView() {
    const product = $('[data-marketplace-product-id]');
    if (!product) return;
    let visitorId = localStorage.getItem('drvn_visitor_id');
    if (!visitorId) { visitorId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`; localStorage.setItem('drvn_visitor_id', visitorId); }
    api('/events', {method:'POST', body:JSON.stringify({type:'LISTING_VIEW', productId:product.dataset.marketplaceProductId, visitorId})}).catch(()=>{});
  }

  function init() {
    $$('[data-marketplace-search]').forEach((root) => new MarketplaceSearch(root));
    $$('[data-contact-unlock]').forEach((root) => new ContactUnlock(root));
    $$('[data-reviews]').forEach((root) => new ReviewsPanel(root));
    $$('[data-report-form]').forEach((form) => new ReportForm(form));
    $$('[data-customer-dashboard]').forEach((root) => new CustomerDashboard(root));
    $$('[data-owner-dashboard]').forEach((root) => new OwnerDashboard(root));
    $$('[data-listing-form]').forEach((form) => new ListingForm(form));
    initialiseFavorites();
    trackView();
  }

  document.addEventListener('DOMContentLoaded', init);
})();
